import { useState, useEffect, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lazy } from '../Lazy/Lazy';
import { findStoreBySlug } from '../../utils/slug';
import { storeService } from '../../services/store.service';
import { ensureCMSUser, getCMSJWT } from '../../services/auth.service';
import { warmServicesStep } from '../../services/servicesStepWarmup';

// Lazy load BookingFlow since this wrapper is already lazy-loaded
const BookingFlow = lazy(() => import('./BookingFlow'));

const storedStore = (): any | null => {
  try {
    const stored = localStorage.getItem('selectedStore');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export default function BookingFlowWrapper() {
  const { step, storeSlug } = useParams<{ step?: string; storeSlug?: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any | null>(null);
  const [selectedStore, setSelectedStore] = useState<any | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [storeNotFound, setStoreNotFound] = useState(false);
  const servicesStep = !step || step === 'services' || step === 'pets';

  // Load user and resolve store (from URL slug or localStorage)
  useEffect(() => {
    // The services step's reads start now, not once its code has loaded and
    // mounted (`servicesStepWarmup.ts`). A link's location is known once resolved below.
    if (servicesStep && !storeSlug) warmServicesStep(storedStore()?.id, !!getCMSJWT());

    // Resolve store: URL slug takes priority, then localStorage fallback
    const resolveStore = async (): Promise<{ store: any | null; notFound: boolean }> => {
      if (!storeSlug) return { store: storedStore(), notFound: false };
      try {
        const stores = await storeService.getStores();
        const matched = findStoreBySlug(stores, storeSlug);
        if (!matched) return { store: null, notFound: true };
        localStorage.setItem('selectedStore', JSON.stringify(matched));
        if (servicesStep) warmServicesStep(matched.id, !!getCMSJWT());
        return { store: matched, notFound: false };
      } catch {
        // API failed — fall back to localStorage
        return { store: storedStore(), notFound: false };
      }
    };

    const init = async () => {
      // Load CMS user data
      // A token means signed in; the user record follows it (fetched and
      // stored slim when it is missing), so this page never shows a
      // signed-in client the public flow because one record was lost.
      // The user and the location don't wait for each other.
      const [cmsUser, resolved] = await Promise.all([ensureCMSUser(), resolveStore()]);
      if (cmsUser) {
        const transformedUser = {
          id: cmsUser.id,
          name: `${cmsUser.firstName || ''} ${cmsUser.lastName || ''}`.trim(),
          email: cmsUser.email || '',
          picture: cmsUser.picture || ''
        };
        setUser(transformedUser);
        setIsAuthenticated(true);
      }
      if (resolved.notFound) setStoreNotFound(true);
      else if (resolved.store) setSelectedStore(resolved.store);

      setIsLoading(false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSlug]);

  const handleLocationSelect = (store: any) => {
    setSelectedStore(store);
    localStorage.setItem('selectedStore', JSON.stringify(store));
  };

  if (storeNotFound) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        gap: '16px'
      }}>
        <p style={{ fontSize: '16px', color: '#1D2129' }}>Store not found.</p>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '10px 24px',
            borderRadius: '50px',
            border: 'none',
            backgroundColor: '#0066FF',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          Go to Home
        </button>
      </div>
    );
  }

  // Show loading state while resolving store and checking authentication
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <Lazy>
      <BookingFlow
        user={user}
        isAuthenticated={isAuthenticated}
        selectedStore={selectedStore}
        onLocationSelect={handleLocationSelect}
      />
    </Lazy>
  );
}
